import * as React from 'react';
import { equals } from 'ramda';
import { v4 } from 'uuid';
import TabContent from 'Components/TabContent';
import TabEditor from '../TabEditor';
import Treatment from '../Treatment';
import { sortPartialDate } from 'neuro-utils';
import ButtonRow from '../ButtonRow';
import { findPrecedingDoctorsOrder, getSessionNumber, subjectOfTreatmentTitle } from 'Utility/ninmtUtil';

const Treatments = ({
  editIndex,
  formData,
  fm,
  doctorsOrders,
}: {
  editIndex: number;
  formData: IFormData<IDocumentWithTypeV2<'tdcs'>>;
  fm: (m: string) => string;
  doctorsOrders?: IDocumentWithTypeV2<'doctorsOrders'>[];
}): React.JSX.Element => {
  const { document, onChange } = formData;

  // Ref for scrolling to top of treatments
  const ref = React.createRef() as React.RefObject<HTMLDivElement>;

  const elementInViewport = (el: HTMLDivElement): boolean => {
    const bb = el.getBoundingClientRect();
    const lowerBound = 80;
    return bb.top >= lowerBound && bb.top <= (window.innerHeight - lowerBound) / 3;
  };

  React.useEffect(() => {
    const newSessions = structuredClone(document.sessions);
    if (newSessions?.[editIndex]?.date && !newSessions[editIndex].subjectOfTreatment) {
      newSessions[editIndex].subjectOfTreatment = [];
      onChange && onChange({ sessions: newSessions });
    }
  }, [document.sessions?.[editIndex]?.date]);

  const treatments =
    Array.isArray(document.sessions?.[editIndex]?.subjectOfTreatment) &&
    document.sessions[editIndex].subjectOfTreatment.filter(s => Boolean(s)).length > 0
      ? document.sessions[editIndex].subjectOfTreatment
      : [{ id: v4(), name: '' }];

  const treatmentOnChange =
    (index: number) =>
    (value: TOnChangeValues): void => {
      let newTreatments = structuredClone(treatments);
      const newSessions = structuredClone(document.sessions);
      // Check if there exists an treatment array with given index
      if (!newSessions?.[editIndex]) return;
      if (Array.isArray(treatments) && treatments.length >= index && Array.isArray(newSessions)) {
        newTreatments[index] = Object.assign({}, newTreatments[index], value);
        newSessions[editIndex].subjectOfTreatment = newTreatments;
        onChange && onChange({ sessions: newSessions });
      } else if (Array.isArray(newSessions)) {
        newTreatments = [Object.assign({ id: v4(), name: '' }, value)];
        newSessions[editIndex].subjectOfTreatment = newTreatments;
        // Treatment array does not exists
        onChange && onChange({ sessions: newSessions });
      }
    };

  const treatmentRemoveRestoreEvent =
    (restore?: true) =>
    (
      selectedTabIndex: number,
      changeSelectedTab?: (i: number) => () => void,
      deleteReason?: ITDCSTreatment['deleteReason'],
    ) => {
      const thisSubjectOfTreatment = structuredClone(treatments);
      const newSessions = structuredClone(document.sessions);
      return () => {
        if (!newSessions?.[editIndex]) return;
        if (
          Array.isArray(thisSubjectOfTreatment) &&
          thisSubjectOfTreatment.length >= selectedTabIndex &&
          Array.isArray(newSessions)
        ) {
          thisSubjectOfTreatment[selectedTabIndex] = {
            ...thisSubjectOfTreatment[selectedTabIndex],
            deleted: restore ? false : true,
            deleteReason: restore ? null : deleteReason,
          } as ITDCSTreatment;
          newSessions[editIndex].subjectOfTreatment = thisSubjectOfTreatment;
          if (onChange) {
            onChange({ sessions: newSessions });
          }
          if (changeSelectedTab && !Boolean(thisSubjectOfTreatment[selectedTabIndex])) {
            const newIndex = Boolean(thisSubjectOfTreatment) ? thisSubjectOfTreatment.length - 1 : 0;
            changeSelectedTab(newIndex)();
          }
          if (!restore && Boolean(ref.current) && !elementInViewport(ref.current)) {
            scroll({ top: ref.current.offsetTop - 75, behavior: 'smooth' });
          }
        }
      };
    };

  const sessions = document.sessions ?? [];
  const sessionsSorted = sessions.toSorted((s1, s2) => sortPartialDate(s2.date, s1.date));
  const currentIndex = sessionsSorted.findIndex(s => equals(sessions[editIndex], s));
  const precedingDoctorsOrder = findPrecedingDoctorsOrder('tdcs', doctorsOrders ?? [], sessionsSorted, currentIndex);

  const subOfTreatTabEditor = <TabEditor editIndex={editIndex} />;
  const subOfTreatButtonRow = (
    <ButtonRow
      editIndex={editIndex}
      remove={treatmentRemoveRestoreEvent()}
      restore={treatmentRemoveRestoreEvent(true)}
    />
  );
  return Array.isArray(treatments) ? (
    <div ref={ref}>
      <TabContent
        tabEditor={subOfTreatTabEditor}
        tabButtonRow={treatments.length > 1 ? subOfTreatButtonRow : undefined}
      >
        {
          treatments.map((s: ITDCSTreatment, i: number) => ({
            key: `${s.id}${i}`,
            id: `${s.id}Tab${i}`,
            title: subjectOfTreatmentTitle(s, 'tdcs', fm),
            ended: s.deleted,
            content:
              // Treatments either have one item with a name or more than one item with/without a name
              (s.name && s.name.length > 0) || treatments.length > 1 ? (
                <Treatment
                  document={s}
                  onChange={treatmentOnChange(i)}
                  session={document.sessions?.[editIndex]}
                  sessionNumber={getSessionNumber(document.sessions ?? [], editIndex, s)}
                  precedingDoctorsOrder={precedingDoctorsOrder}
                />
              ) : (
                <div style={{ padding: '2rem 0 2rem 0' }}>{fm('tdcs.subjectOfTreatment.copyDescription')}</div>
              ),
            count: i,
          })) as Parameters<typeof TabContent>[0]['children']
        }
      </TabContent>
    </div>
  ) : (
    <></>
  );
};

export default Treatments;
